import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react';

import {
  IconAlertTriangle,
  IconLoader2,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';

import {
  ButtonField,
  ButtonGroup,
  ButtonGroupItem,
  CheckboxField,
  Iconsvg,
  InputField,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@wk/components-react16';

import useToast from '@/shared/stores/toastStore';
import {
  createRole,
  deleteRole,
  getAssignableRoles,
  updateRole,
} from '@/domains/access/userAccessApi';
import type { AppRole } from '@/types';

const apiErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as AxiosError | undefined)?.response?.data as
    | { title?: string; message?: string }
    | undefined;
  return data?.title || data?.message || fallback;
};

type DraftRole = { value: string; displayName: string; description: string };
const EMPTY_DRAFT: DraftRole = { value: '', displayName: '', description: '' };

type EditDraft = { displayName: string; description: string; isEnabled: boolean };

const RolesTab = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [filterTerm, setFilterTerm] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState<DraftRole>(EMPTY_DRAFT);
  const [editTarget, setEditTarget] = useState<AppRole | null>(null);
  const [editDraft, setEditDraft] = useState<EditDraft>({
    displayName: '',
    description: '',
    isEnabled: true,
  });
  const [deleteTarget, setDeleteTarget] = useState<AppRole | null>(null);

  // Below the `sm` breakpoint AG Grid has no height to fill, so render a card list instead
  // (mirrors the Users tab).
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window === 'undefined'
      ? false
      : window.matchMedia('(max-width: 640px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobileViewport(e.matches);
    setIsMobileViewport(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const rolesQ = useQuery({
    queryKey: ['user-access', 'roles'],
    queryFn: async () => (await getAssignableRoles()).data,
    staleTime: 60 * 60 * 1000,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['user-access', 'roles'] });

  const roles = rolesQ.data ?? [];
  const filteredRoles = useMemo(() => {
    const needle = filterTerm.trim().toLowerCase();
    return [...roles]
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .filter(
        (r) =>
          !needle ||
          r.value.toLowerCase().includes(needle) ||
          r.displayName.toLowerCase().includes(needle),
      );
  }, [roles, filterTerm]);

  const createMutation = useMutation({
    mutationFn: async (body: DraftRole) =>
      (
        await createRole({
          value: body.value.trim(),
          displayName: body.displayName.trim() || undefined,
          description: body.description.trim() || undefined,
        })
      ).data,
    onSuccess: (created) => {
      invalidate();
      showToast('success', {
        title: 'Role created',
        message: `${created.displayName} created. It may take a moment to become assignable.`,
      });
      setCreateOpen(false);
      setDraft(EMPTY_DRAFT);
    },
    onError: (error) =>
      showToast('error', {
        title: 'Could not create role',
        message: apiErrorMessage(error, 'An error occurred while creating the role.'),
      }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: EditDraft }) =>
      (await updateRole(id, body)).data,
    onSuccess: (updated) => {
      invalidate();
      showToast('success', {
        title: 'Role updated',
        message: `${updated.displayName} updated.`,
      });
      setEditTarget(null);
    },
    onError: (error) =>
      showToast('error', {
        title: 'Could not update role',
        message: apiErrorMessage(error, 'An error occurred while updating the role.'),
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => deleteRole(id),
    onSuccess: (_data, id) => {
      invalidate();
      const name = roles.find((r) => r.id === id)?.displayName ?? id;
      showToast('success', {
        title: 'Role deleted',
        message: `${name} removed. Existing assignments are dropped by Entra.`,
      });
      setDeleteTarget(null);
    },
    onError: (error) =>
      showToast('error', {
        title: 'Could not delete role',
        message: apiErrorMessage(error, 'An error occurred while deleting the role.'),
      }),
  });

  const openEdit = (role: AppRole) => {
    setEditDraft({
      displayName: role.displayName,
      description: role.description,
      isEnabled: true,
    });
    setEditTarget(role);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="cg-field flex min-w-0 flex-1 flex-col sm:max-w-xs">
          <label className="cg-field-label" htmlFor="role-filter">
            Filter
          </label>
          <div className="group border border-input text-sm hover:border-[#005b92] focus-within:border-[#000]">
            <div className="flex min-h-8 items-center gap-1 px-[15px] py-1">
              <input
                id="role-filter"
                type="text"
                value={filterTerm}
                onChange={(e) => setFilterTerm(e.target.value)}
                placeholder="Filter by name or value"
                autoComplete="off"
                className="ml-2 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
              />
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:ml-6">
          <ButtonField mode="text">
            <button
              type="button"
              aria-label="Create Role"
              onClick={() => {
                setDraft(EMPTY_DRAFT);
                setCreateOpen(true);
              }}
            >
              <span className="flex items-center gap-2">
                <Iconsvg name="plus" />
                <span className="max-sm:hidden">Create Role</span>
              </span>
            </button>
          </ButtonField>
        </div>
      </div>

      {isMobileViewport ? (
        <div className="flex flex-col gap-3">
          {rolesQ.isLoading ? (
            <div className="flex min-h-[120px] items-center justify-center text-gray-500">
              <IconLoader2 size={20} className="animate-spin" />
            </div>
          ) : filteredRoles.length === 0 ? (
            <div className="rounded-md border bg-white p-4 text-sm text-gray-600">
              No roles found.
            </div>
          ) : (
            filteredRoles.map((r) => (
              <article key={r.id} className="rounded-lg border bg-white p-3 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="break-words font-semibold leading-5">{r.displayName}</p>
                    {r.value !== r.displayName ? (
                      <p className="text-xs text-gray-500">{r.value}</p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      aria-label={`Edit ${r.displayName}`}
                      onClick={() => openEdit(r)}
                      className="flex h-7 w-7 items-center justify-center text-gray-500 hover:text-gray-800"
                    >
                      <IconPencil size={16} />
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${r.displayName}`}
                      onClick={() => setDeleteTarget(r)}
                      className="flex h-7 w-7 items-center justify-center text-gray-500 hover:text-red-600"
                    >
                      <IconTrash size={16} />
                    </button>
                  </div>
                </div>
                {r.description ? (
                  <p className="mt-2 text-sm text-gray-600">{r.description}</p>
                ) : null}
              </article>
            ))
          )}
        </div>
      ) : (
      <div className="cg-ag-grid-field ag-theme-balham min-h-0 flex-1">
        <AgGridReact
          animateRows={false}
          loading={rolesQ.isLoading}
          rowData={filteredRoles}
          columnDefs={[
            {
              headerName: 'Role',
              field: 'displayName',
              flex: 2,
              minWidth: 240,
              autoHeight: true,
              cellRenderer: (p: CustomCellRendererProps<AppRole>) => {
                const r = p.data;
                if (!r) return null;
                return (
                  <div className="flex flex-col py-1 leading-tight">
                    <span className="font-medium">{r.displayName}</span>
                    {r.value !== r.displayName ? (
                      <span className="block truncate text-xs text-gray-500">
                        {r.value}
                      </span>
                    ) : null}
                  </div>
                );
              },
            },
            {
              headerName: 'Description',
              field: 'description',
              flex: 3,
              minWidth: 280,
              sortable: false,
              cellRenderer: (p: CustomCellRendererProps<AppRole>) => (
                <span className="text-sm text-gray-600">
                  {p.data?.description || '—'}
                </span>
              ),
            },
            {
              headerName: 'Actions',
              colId: 'actions',
              width: 110,
              sortable: false,
              cellRenderer: (p: CustomCellRendererProps<AppRole>) => {
                const r = p.data;
                if (!r) return null;
                return (
                  <div className="flex h-full items-center gap-1">
                    <button
                      type="button"
                      aria-label={`Edit ${r.displayName}`}
                      title="Edit Role"
                      onClick={() => openEdit(r)}
                      className="flex h-7 w-7 cursor-pointer items-center justify-center text-gray-500 hover:text-gray-800"
                    >
                      <IconPencil size={16} />
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${r.displayName}`}
                      title="Delete Role"
                      onClick={() => setDeleteTarget(r)}
                      className="flex h-7 w-7 cursor-pointer items-center justify-center text-gray-500 hover:text-red-600"
                    >
                      <IconTrash size={16} />
                    </button>
                  </div>
                );
              },
            },
          ]}
        />
      </div>
      )}
      <p className="text-sm text-muted-foreground">{filteredRoles.length} role(s)</p>

      {/* Create role */}
      <Modal
        isOpen={createOpen}
        mobileMode="fullscreen"
        srLabelledBy="create-role-title"
        srDescribedBy="create-role-desc"
        onUserRequest={() => setCreateOpen(false)}
      >
        <ModalHeader titleId="create-role-title" onUserRequest={() => setCreateOpen(false)}>
          Create Role
        </ModalHeader>
        <ModalBody descriptionId="create-role-desc">
          <div className="flex flex-col gap-3">
            <InputField label="Value">
              <input
                type="text"
                name="role-value"
                value={draft.value}
                autoComplete="off"
                onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))}
              />
            </InputField>
            <InputField label="Display name (optional — defaults to value)">
              <input
                type="text"
                name="role-display"
                value={draft.displayName}
                autoComplete="off"
                onChange={(e) =>
                  setDraft((d) => ({ ...d, displayName: e.target.value }))
                }
              />
            </InputField>
            <InputField label="Description (optional)">
              <input
                type="text"
                name="role-desc"
                value={draft.description}
                autoComplete="off"
                onChange={(e) =>
                  setDraft((d) => ({ ...d, description: e.target.value }))
                }
              />
            </InputField>
            <p className="text-xs text-gray-500">
              A new role takes a short while to propagate in Entra before it can be
              assigned to users.
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup mode="static">
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField mode="text">
                <button
                  type="button"
                  onClick={() => setCreateOpen(false)}
                  disabled={createMutation.isPending}
                >
                  Cancel
                </button>
              </ButtonField>
            </ButtonGroupItem>
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField>
                <button
                  type="button"
                  disabled={createMutation.isPending || !draft.value.trim()}
                  onClick={() => createMutation.mutate(draft)}
                >
                  Create
                  {createMutation.isPending ? (
                    <IconLoader2 size={16} className="animate-spin" />
                  ) : null}
                </button>
              </ButtonField>
            </ButtonGroupItem>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      {/* Edit role */}
      <Modal
        isOpen={editTarget !== null}
        mobileMode="fullscreen"
        srLabelledBy="edit-role-title"
        srDescribedBy="edit-role-desc"
        onUserRequest={() => setEditTarget(null)}
      >
        <ModalHeader titleId="edit-role-title" onUserRequest={() => setEditTarget(null)}>
          Edit Role
        </ModalHeader>
        <ModalBody descriptionId="edit-role-desc">
          {editTarget ? (
            <div className="flex flex-col gap-3">
              <div>
                <p className="font-medium">{editTarget.value}</p>
                <p className="text-xs text-gray-500">The role value is immutable.</p>
              </div>
              <InputField label="Display name">
                <input
                  type="text"
                  name="edit-role-display"
                  value={editDraft.displayName}
                  autoComplete="off"
                  onChange={(e) =>
                    setEditDraft((d) => ({ ...d, displayName: e.target.value }))
                  }
                />
              </InputField>
              <InputField label="Description">
                <input
                  type="text"
                  name="edit-role-desc"
                  value={editDraft.description}
                  autoComplete="off"
                  onChange={(e) =>
                    setEditDraft((d) => ({ ...d, description: e.target.value }))
                  }
                />
              </InputField>
              <CheckboxField label="Enabled (uncheck to hide the role from assignment)">
                <input
                  type="checkbox"
                  name="edit-role-enabled"
                  checked={editDraft.isEnabled}
                  onChange={(e) =>
                    setEditDraft((d) => ({ ...d, isEnabled: e.target.checked }))
                  }
                />
              </CheckboxField>
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup mode="static">
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField mode="text">
                <button
                  type="button"
                  onClick={() => setEditTarget(null)}
                  disabled={updateMutation.isPending}
                >
                  Cancel
                </button>
              </ButtonField>
            </ButtonGroupItem>
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField>
                <button
                  type="button"
                  disabled={updateMutation.isPending}
                  onClick={() =>
                    editTarget &&
                    updateMutation.mutate({ id: editTarget.id, body: editDraft })
                  }
                >
                  Apply
                  {updateMutation.isPending ? (
                    <IconLoader2 size={16} className="animate-spin" />
                  ) : null}
                </button>
              </ButtonField>
            </ButtonGroupItem>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      {/* Delete confirm */}
      <Modal
        isOpen={deleteTarget !== null}
        mobileMode="fullscreen"
        srLabelledBy="delete-role-title"
        srDescribedBy="delete-role-desc"
        onUserRequest={() => setDeleteTarget(null)}
      >
        <ModalHeader titleId="delete-role-title" onUserRequest={() => setDeleteTarget(null)}>
          Delete Role
        </ModalHeader>
        <ModalBody descriptionId="delete-role-desc">
          <div className="flex items-start gap-2 text-sm text-gray-700">
            <IconAlertTriangle size={20} className="mt-0.5 shrink-0 text-red-500" />
            <span>
              Delete <span className="font-semibold">{deleteTarget?.displayName}</span>?
              Entra removes the role and drops every existing assignment. Any view that
              requires this role becomes inaccessible until reassigned.
            </span>
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup mode="static">
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField mode="text">
                <button type="button" onClick={() => setDeleteTarget(null)}>
                  Cancel
                </button>
              </ButtonField>
            </ButtonGroupItem>
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField>
                <button
                  type="button"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
                >
                  Delete
                  {deleteMutation.isPending ? (
                    <IconLoader2 size={16} className="animate-spin" />
                  ) : null}
                </button>
              </ButtonField>
            </ButtonGroupItem>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default RolesTab;
